// Products.jsx
import React, { useState, useEffect, useRef } from "react";
import "../../styles/PagesStyles/Products.css";
import { trackEvent } from "../../utils/google-analytics";

import img6 from "../../assets/images/Products/handshake.jpg";
import img7 from "../../assets/images/Products/shield.png";
import img8 from "../../assets/images/Products/file.png";
import img9 from "../../assets/images/Products/message.png";
import img10 from "../../assets/images/Products/edgecomputing.png";
import img11 from "../../assets/images/Products/bot.png";
import usePageTitle from "../../hooks/usePageTitle";


const products = [
  {
    id: 1,
    title: "RiskGen",
    subtitle: "Explainable, compliance-aligned risk decisions",
    emoji: img7,
    description:
      "AI-powered risk assessment that provides transparent reasoning and audit trails for every decision.",
    features: [
      "Real-time risk scoring with explainable factors",
      "Regulatory compliance documentation",
      "Predictive risk modeling",
      "Automated compliance checks",
    ],
  },
  {
    id: 2,
    title: "DocuGen",
    subtitle: "Enterprise document intelligence",
    emoji: img8,
    description:
      "Transform unstructured documents into actionable data with enterprise-grade accuracy.",
    features: [
      "Multi-format document processing",
      "Automated data extraction pipelines",
      "Intelligent document classification",
      "Secure document storage & retrieval",
    ],
  },
  {
    id: 3,
    title: "ChatGen",
    subtitle: "Secure conversational AI for workflows",
    emoji: img9,
    description:
      "Enterprise-ready conversational AI that integrates seamlessly with existing workflows.",
    features: [
      "Context-aware conversation memory",
      "Enterprise SSO and permissions",
      "Multi-language support",
      "Real-time collaboration features",
    ],
  },
  {
    id: 4,
    title: "DataGen",
    subtitle: "Synthetic data generation platform",
    emoji: img10,
    description:
      "Generate high-quality synthetic data for testing, development, and AI training while preserving privacy.",
    features: [
      "Privacy-preserving synthetic data",
      "Multi-format data generation",
      "Data quality validation",
      "Enterprise data compliance",
    ],
  },
  {
    id: 5,
    title: "Enterprise Copilot Framework",
    subtitle:
      "Provides the foundation to deploy AI copilots that work alongside teams",
    emoji: img11,
    description:
      "Adapts to roles, workflows, and permissions—making AI assistance practical and governed.",
    features: [
      "Role-based AI copilots",
      "Built-in governance and access control",
      "Workflow automation integration",
      "Adaptive AI assistance",
    ],
  },
];

/* ================= PROCESS TIMELINE ================= */
const processSteps = [
  {
    title: "Discovery",
    text:
      "We analyze your business goals, challenges, and operational needs. This phase ensures clear problem definition and strategic alignment before development begins.",
    side: "right",
  },
  {
    title: "Validate",
    text:
      "We test and refine solutions using real-world data and continuous feedback. This ensures accuracy, reliability, and proven performance before deployment.",
    side: "left",
  },
  {
    title: "Technology",
    text:
      "Validated solutions are seamlessly integrated into your systems. Our scalable and secure architecture ensures smooth adoption and future readiness.",
    side: "right",
  },
  {
    title: "Improve",
    text:
      "We continuously monitor performance and optimize based on insights. Ongoing support ensures sustained value and long-term success.",
    side: "left",
  },
];

const Products = () => {
  usePageTitle("Combat Solutions AI | Products");

  const [currentIndex, setCurrentIndex] = useState(0);
  const [flippedCard, setFlippedCard] = useState(null);

  const sliderTrackRef = useRef(null);
  const sliderInnerRef = useRef(null);
  const timelineItemsRef = useRef([]);
const timelineRef = useRef(null);
const progressRef = useRef(null);
const itemsRef = useRef([]);
useEffect(() => {
  const section = timelineRef.current;
  const progress = progressRef.current;
  const items = itemsRef.current;

  if (!section || !progress) return;

  const handleScroll = () => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;

    const sectionTop = section.offsetTop;
    const sectionHeight = section.offsetHeight;

    // Define scroll range
    const start = sectionTop - windowHeight * 0.6;
    const end = sectionTop + sectionHeight - windowHeight * 0.6;

    // Clamp progress between 0 and 1
    const progressValue = Math.min(
      Math.max((scrollY - start) / (end - start), 0),
      1
    );

    /* ================= LINE ================= */
    progress.style.height = `${progressValue * 100}%`;

    /* ================= CARDS ================= */
    const totalItems = items.length;

    items.forEach((item, index) => {
      const itemTrigger = (index + 0.2) / totalItems;

      if (progressValue >= itemTrigger) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  };

  window.addEventListener("scroll", handleScroll);
  handleScroll();

  return () => window.removeEventListener("scroll", handleScroll);
}, []);


  /* ---------------- HELPER ---------------- */
  const getCardsPerView = () => {
    const width = window.innerWidth;
    if (width < 768) return 1;      // mobile
    if (width < 1025) return 2;     // tablet / iPad
    return 3;                       // desktop
  };

  /* ---------------- SLIDER LOGIC ---------------- */
  const slide = (direction) => {
    const track = sliderTrackRef.current;
    const inner = sliderInnerRef.current;
    if (!track || !inner) return;

    const cardsPerView = getCardsPerView();
    const maxIndex = products.length - cardsPerView;

    setCurrentIndex((prev) => {
      let nextIndex =
        direction === "next" ? prev + 1 : prev - 1;

      if (nextIndex < 0) nextIndex = 0;
      if (nextIndex > maxIndex) nextIndex = maxIndex;

      const cardWidth = track.offsetWidth / cardsPerView;
      inner.style.transform = `translateX(-${
        nextIndex * cardWidth
      }px)`;

      return nextIndex;
    });

    setFlippedCard(null);

    trackEvent("slider_navigation", {
      page: "Products Page",
      direction: "previous",
    });
  };

  /* ---------------- RESIZE HANDLING ---------------- */
  useEffect(() => {
    const handleResize = () => {
      setCurrentIndex(0);
      if (sliderInnerRef.current) {
        sliderInnerRef.current.style.transform = "translateX(0px)";
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------------- CARD HANDLERS ---------------- */
  const handleCardClick = (id, e) => {
    if (e.target.closest(".slider-btn")) return;
    setFlippedCard(flippedCard === id ? null : id);
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    setFlippedCard(null);
  };

  /* ---------------- JSX ---------------- */
  return (
    <div className="products-wrapper">
      {/* Hero */}
      <section
        className="product-hero"
        style={{ backgroundImage: `url(${img6})` }}
      >
        <div className="hero-overlay" />
        <div className="product-hero-content">
          <h1 className="product-title">
            Enterprise AI, Built for Impact
          </h1>
          <p className="product-description">
            Secure, production-ready AI solutions that combine human
            judgment with machine intelligence.
          </p>
        </div>
      </section>

      {/* Products */}
      <div className="products-cards-section">
        <div className="section-intro">
          <h2>Products</h2>
          <p className="intro-subtitle">
            Production-ready AI solutions that integrate seamlessly with
            your infrastructure
          </p>
        </div>

        <div className="slider-container">
          {/* Prev */}
          <button
            className="slider-btn prev-btn"
            onClick={() => slide("prev")}
            aria-label="Previous"
          >
            ‹
          </button>

          {/* Track */}
       <div className="slider-track" ref={sliderTrackRef}>
  <div className="slider-inner" ref={sliderInnerRef}>
    {products.map((product) => (
      <div
        key={product.id}
        className="slider-card-container"
        onClick={(e) => handleCardClick(product.id, e)}
      >
        <div
          className={`hover-card ${
            flippedCard === product.id ? "flipped" : ""
          }`}
        >
          {/* FRONT */}
          <div className="card-front">
            <div className="card-thumbnail">
              <img
                src={product.emoji}
                alt={product.title}
                className="thumbnail-emoji"
              />
            </div>

            <h3 className="card-title">{product.title}</h3>
            <p className="card-subtitle">{product.subtitle}</p>

            <div className="hover-hint">
              <span>Hover to explore →</span>
            </div>
            <div className="mobile-tap-hint">
              <span>Tap to explore →</span>
            </div>
          </div>

          {/* BACK */}
          <div className="card-back">
            <div className="back-content">
              <h3 className="back-title">{product.title}</h3>
              <p className="back-description">{product.description}</p>

              <div className="features-list">
                {product.features.map((f, i) => (
                  <div key={i} className="feature-item">
                    <span className="feature-bullet">•</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <button
                className="mobile-close-btn"
                onClick={handleCloseClick}
              >
                Close
              </button>
            </div>
          </div>

          <div className="card-accent-bar" />
        </div>
      </div>
    ))}
  </div>
</div>


          {/* Next */}
          <button
            className="slider-btn next-btn"
            onClick={() => slide("next")}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      </div>

 
     {/* ================= PROCESS TIMELINE ================= */}
<section className="process-timeline" ref={timelineRef}>
  <div className="process-intro">
          <h2>Product Life Cycle</h2>
          <p className="intro-subtitle">
            From discovery to deployment building secure, production-ready AI solutions
          </p>
        </div>
  <div className="timeline-line">
    <div className="timeline-progress" ref={progressRef} />
  </div>

  {processSteps.map((step, index) => (
    <div
      key={index}
      ref={(el) => (itemsRef.current[index] = el)}
      className={`timeline-item ${step.side}`}
    >
      <div className="timeline-dot" />
      <div className="timeline-card">
        <h3>{step.title}</h3>
        <p>{step.text}</p>
      </div>
    </div>
  ))}
</section>

    </div>
  );
};

export default Products;